/** @internal – import from `@/store/useDepartmentStore` instead. */
import type { SocialBrandInput } from "../types";
import { runSSEStream } from "../sseRunner";
import { buildErrorMessage } from "../utils";
import { sseEvents } from "../../../events";

/**
 * Social-research SSE pipeline runner. Posts to
 * `/core/coagent/social-research` and publishes typed `social:*` events.
 *
 * Migrated off the `SSECallbacks` mutator-bag in Phase 8 commit 74.
 */
export async function runSocialResearchSSE(
  brandInput: SocialBrandInput,
  sessionId: string,
  signal?: AbortSignal,
  // BrandConfig.identity.id — when present, the persisted social:research
  // artifact is tagged so /brands/:id surfaces every social brief run
  // against this brand.
  brandId?: string,
): Promise<void> {
  await runSSEStream(brandInput, {
    pipeline: "social-research",
    endpoint: "/core/coagent/social-research",
    sessionId,
    buildBody: (sid, input) => ({
      sessionId: sid,
      brandInput: input,
      ...(brandId ? { brandId } : {}),
    }),
    errorPrefix: "Social research",
    signal,
    debugMeta: { brandName: brandInput.brandName, hasAbortSignal: Boolean(signal) },
    handleEvent: (event) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { phase, status, data, error, details } = event as Record<string, any>;
      const ev = data as Record<string, unknown> | undefined;

      if (phase === "session_named" && ev?.name) {
        sseEvents.publish({
          type: "social:session-named",
          pipeline: "social-research",
          sessionId,
          name: ev.name as string,
        });
        return;
      }

      if (phase === "query_plan_generation" && status === "complete") {
        sseEvents.publish({
          type: "social:query-plan-complete",
          pipeline: "social-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "social_data_collection" && status === "running") {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const progress = (event as any).progress;
        sseEvents.publish({
          type: "social:data-collection-progress",
          pipeline: "social-research",
          sessionId,
          track: progress?.track || "social_data_collection",
          completed: Number(progress?.completed ?? 0),
          total: Number(progress?.total ?? 0),
        });
        return;
      }

      if (phase === "social_data_collection" && status === "complete") {
        sseEvents.publish({
          type: "social:data-collection-complete",
          pipeline: "social-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "distillation" && status === "complete") {
        sseEvents.publish({
          type: "social:distillation-complete",
          pipeline: "social-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "trend_analysis" && status === "complete") {
        sseEvents.publish({
          type: "social:trend-analysis-complete",
          pipeline: "social-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "competitor_audit" && status === "complete") {
        sseEvents.publish({
          type: "social:competitor-audit-complete",
          pipeline: "social-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "template_generation" && status === "complete") {
        sseEvents.publish({
          type: "social:template-generation-complete",
          pipeline: "social-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "calendar_generation" && status === "complete") {
        sseEvents.publish({
          type: "social:calendar-generation-complete",
          pipeline: "social-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "brief_generation" && status === "complete") {
        sseEvents.publish({
          type: "social:brief-generation-complete",
          pipeline: "social-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "done") {
        sseEvents.publish({
          type: "social:done",
          pipeline: "social-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "error") {
        const message = buildErrorMessage(error, details, "Social research pipeline failed");
        console.error("[coagent/social-research] SSE error", { sessionId, message, details });
        sseEvents.publish({
          type: "social:error",
          pipeline: "social-research",
          sessionId,
          message,
          ...(details !== undefined ? { details } : {}),
        });
      }
    },
  });
}
